import { useState } from 'react';
import { supabase } from '../../lib/supabase';
import { getGardenName, getGardenSlug } from '../../lib/garden';
import { useGardenMeta } from '../../lib/useGardenMeta';
import { SCALE4, SCALE5, INTEREST } from '../../lib/reviewLabels';

/**
 * 帰り道のアンケート（ワーカーさん → 園）。
 *
 * 🟥 目的は粗探しではない。多賀城の報告がゴール像：
 *    「遊びも私達の発想にはなかったことがたくさんあり、学べる良い機会になった」
 *    「何度か来ていただいた方から『きらりで働きたい』とお話しあり、入社された方もいます」
 *    ＝ ①教えてもらう ②また来たい・働きたいに繋げる。改善はその結果としてついてくる。
 *
 * 🟥 冒頭の「タイミーの評価には使いません」を外さない。
 *    これが無いと「悪く書いたら次から呼ばれない」と思われて、本音が出ない。
 *
 * 🟥 勤務のあとに答えてもらう。疲れている。だから
 *    ・全部任意（ひとつも書かずに送れる）
 *    ・選ぶだけで終われる
 *    ・2分で終わる分量に収める
 *
 * 🟥 デザインはすっきり（2026-09-25 後藤さん）。白地・線は細く・色は1つだけ。
 * 🟥 あおいが案内する（2026-09-25 後藤さん「あおいがいないぞ」）。
 *    あおいは hoiku-station 全体のAIキャラクターで、役どころは現場アシスタント。
 *    園の人が説明係にならなくていいように、あおいが代わりに話しかける。
 *    質問文そのものは素のまま置き、あおいは入口と出口だけに立つ。
 *    全部をセリフにすると読みづらくなり、すっきりが壊れる。
 * 🟥 あおいの絵は正本（kirari-method/tools/aoi-visual/aoi_reference.png）をそのまま置く。
 *    顔だけに切り出さない。切ると別人に見える（2026-09-25 後藤さん「正本そのままで良いだろ」）。
 */

const GOLD = '#a8935f';

function Choice({
  value,
  onChange,
  options,
}: {
  value: number | null;
  onChange: (v: number) => void;
  options: { v: number; label: string }[];
}) {
  return (
    <div className="space-y-2.5">
      {options.map((o) => {
        const on = value === o.v;
        return (
          <button
            key={o.v}
            type="button"
            onClick={() => onChange(o.v)}
            className="w-full flex items-center gap-3 text-left rounded-xl border px-4 py-3.5 text-[16px] shadow-sm transition-all active:scale-[0.98] active:shadow-none"
            style={{
              borderColor: on ? GOLD : '#e2ded4',
              borderWidth: on ? 2 : 1,
              background: on ? '#faf7f0' : '#fff',
              color: on ? '#3a3226' : '#5b5245',
              fontWeight: on ? 700 : 400,
              // 選ばれている行は枠が2pxになる分、そのぶん内側の余白を削って
              // 大きさが変わって見えないようにする
              padding: on ? '13px 15px' : '14px 16px',
            }}
          >
            <span
              className="shrink-0 rounded-full flex items-center justify-center transition-colors"
              style={{
                width: 20,
                height: 20,
                border: `2px solid ${on ? GOLD : '#cdc6b7'}`,
                background: on ? GOLD : 'transparent',
              }}
            >
              {on && <span className="w-2 h-2 rounded-full bg-white" />}
            </span>
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="py-7 border-t" style={{ borderColor: '#ece7dc' }}>
      <h2 className="text-[17px] font-bold text-[#3a3226] leading-snug mb-1">{label}</h2>
      {hint && <p className="text-[14.5px] text-[#8a8175] leading-relaxed mb-3.5">{hint}</p>}
      {!hint && <div className="mb-3.5" />}
      {children}
    </section>
  );
}

export default function ReviewPage() {
  useGardenMeta('帰り道のアンケート', 'いつもありがとうございます。今日感じたことを、少しだけ教えてください。');
  const gardenSlug = getGardenSlug();
  const gardenName = getGardenName(gardenSlug);

  const [arrival, setArrival] = useState<number | null>(null);
  const [whom, setWhom] = useState<number | null>(null);
  const [things, setThings] = useState<number | null>(null);
  const [goodMoment, setGoodMoment] = useState('');
  const [idea, setIdea] = useState('');
  const [concern, setConcern] = useState('');
  const [again, setAgain] = useState<number | null>(null);
  const [interest, setInterest] = useState<string | null>(null);
  const [contactName, setContactName] = useState('');
  const [contact, setContact] = useState('');

  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  const submit = async () => {
    setSending(true);
    setError('');
    try {
      const { error: e } = await supabase.from('worker_reviews').insert({
        garden_slug: gardenSlug,
        q_arrival: arrival,
        q_whom_to_ask: whom,
        q_where_things: things,
        good_moment: goodMoment.trim() || null,
        idea: idea.trim() || null,
        concern: concern.trim() || null,
        want_again: again,
        interest,
        contact_name: contactName.trim() || null,
        contact: contact.trim() || null,
      });
      if (e) throw e;
      setDone(true);
      window.scrollTo(0, 0);
    } catch (err) {
      setError('送信できませんでした。電波のよいところで、もう一度お試しください。');
    } finally {
      setSending(false);
    }
  };

  const ta =
    'w-full rounded-xl border px-4 py-3 text-[16px] leading-relaxed text-[#3a3226] placeholder:text-[#b5aea2] focus:outline-none';

  if (done) {
    return (
      <main className="min-h-screen bg-white px-5 py-16">
        <div className="mx-auto max-w-[560px] text-center">
          <img
            src="/images/aoi.jpg"
            alt="あおい"
            className="w-full rounded-2xl mb-6"
          />
          <p className="text-[15px] font-bold mb-2" style={{ color: GOLD }}>
            {gardenName}
          </p>
          <h1 className="text-[24px] font-bold text-[#3a3226] leading-snug mb-5">
            ありがとうございました
          </h1>
          <p className="text-[16px] leading-[1.9] text-[#5b5245]">
            いただいたお話は、園のみんなで読ませていただきます。
            <br />
            本日はお疲れさまでした。
            <br />
            またお会いできる日を、楽しみにしております。
          </p>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-white">
      <div className="mx-auto max-w-[560px] px-5 pb-24 pt-10">
        <p className="text-[15px] font-bold mb-2" style={{ color: GOLD }}>
          {gardenName}
        </p>
        <h1 className="text-[25px] font-bold text-[#3a3226] leading-snug mb-6">
          本日はありがとうございました
        </h1>

        {/* あおいが案内する。園の人が説明係にならなくていいように */}
        <img
          src="/images/aoi.jpg"
          alt="あおい"
          className="w-full rounded-2xl mb-4"
        />
        <div>
          <div className="grow min-w-0">
            <p className="text-[14px] font-bold text-[#8a8175] mb-1.5">きらり保育園　あおい</p>
            <div
              className="rounded-2xl px-4 py-3.5 text-[16px] leading-[1.9]"
              style={{ background: '#faf7f0', color: '#5b5245' }}
            >
              お疲れさまでした。わたしは、きらり保育園のあおいと申します。
              <br />
              たくさんの園をご覧になっているみなさまにしか、見えないことがあります。
              気づかれたことを、教えていただけませんか。
            </div>
          </div>
        </div>

        <div
          className="mt-4 rounded-xl px-4 py-4 text-[15px] leading-[1.85] border"
          style={{ borderColor: '#ece7dc', color: '#5b5245' }}
        >
          <b className="text-[#3a3226]">タイミーの評価には、一切使いません。</b>
          <br />
          お名前は任意です。答えたくない質問は、とばしてください。
          <br />
          すべて任意です。2分ほどで終わります。
        </div>

        <Field label="着いてから、何をすればいいか分かりましたか">
          <Choice value={arrival} onChange={setArrival} options={SCALE4} />
        </Field>

        <Field label="困ったとき、誰に聞けばいいか分かりましたか">
          <Choice value={whom} onChange={setWhom} options={SCALE4} />
        </Field>

        <Field label="物の場所は分かりましたか" hint="エプロン、おむつ、掃除道具など">
          <Choice value={things} onChange={setThings} options={SCALE4} />
        </Field>

        <Field
          label="今日、お子さんとの関わりで「うまくいったな」と思った場面はありますか"
          hint="ちょっとしたことで構いません。園の学びになります。"
        >
          <textarea
            value={goodMoment}
            onChange={(e) => setGoodMoment(e.target.value)}
            rows={4}
            className={ta}
            style={{ borderColor: '#e2ded4' }}
            placeholder="例：なかなか寝ない子に、背中をこの速さでとんとんしたら落ち着いた"
          />
        </Field>

        <Field
          label="ほかの園でやっていて、ここにも合いそうな遊びや工夫はありますか"
          hint="私たちの発想にないことを、いちばん知りたいと思っています。"
        >
          <textarea
            value={idea}
            onChange={(e) => setIdea(e.target.value)}
            rows={4}
            className={ta}
            style={{ borderColor: '#e2ded4' }}
            placeholder="例：雨の日の室内遊びで、こんなことをしている園がありました"
          />
        </Field>

        <Field
          label="気になったところ、やりにくかったところはありますか"
          hint="遠慮なくお書きください。直すために伺っています。"
        >
          <textarea
            value={concern}
            onChange={(e) => setConcern(e.target.value)}
            rows={4}
            className={ta}
            style={{ borderColor: '#e2ded4' }}
            placeholder="例：◯◯がどこにあるか、最後まで分からなかった"
          />
        </Field>

        <Field label="また来たいと思いますか">
          <Choice value={again} onChange={setAgain} options={SCALE5} />
        </Field>

        <Field
          label={`${gardenName}で働くことに、興味はありますか`}
          hint="直接雇用のご相談もできます。いまのお気持ちで構いません。"
        >
          <div className="space-y-2">
            {INTEREST.map((o) => {
              const on = interest === o.v;
              return (
                <button
                  key={o.v}
                  type="button"
                  onClick={() => setInterest(o.v)}
                  className="w-full flex items-center gap-3 text-left rounded-xl border px-4 py-3.5 text-[16px] shadow-sm transition-all active:scale-[0.98] active:shadow-none"
                  style={{
                    borderColor: on ? GOLD : '#e2ded4',
                    borderWidth: on ? 2 : 1,
                    background: on ? '#faf7f0' : '#fff',
                    color: on ? '#3a3226' : '#5b5245',
                    fontWeight: on ? 700 : 400,
                    padding: on ? '13px 15px' : '14px 16px',
                  }}
                >
                  <span
                    className="shrink-0 rounded-full flex items-center justify-center transition-colors"
                    style={{
                      width: 20,
                      height: 20,
                      border: `2px solid ${on ? GOLD : '#cdc6b7'}`,
                      background: on ? GOLD : 'transparent',
                    }}
                  >
                    {on && <span className="w-2 h-2 rounded-full bg-white" />}
                  </span>
                  {o.label}
                </button>
              );
            })}
          </div>

          {(interest === 'yes' || interest === 'maybe') && (
            <div className="mt-4 space-y-3">
              <p className="text-[14.5px] text-[#8a8175] leading-relaxed">
                こちらからご連絡してもよろしければ、連絡先をどうぞ。
                <b className="text-[#3a3226]">書かなくても送信できます。</b>
              </p>
              <input
                value={contact}
                onChange={(e) => setContact(e.target.value)}
                className={ta}
                style={{ borderColor: '#e2ded4' }}
                placeholder="電話番号またはメール（任意）"
              />
            </div>
          )}
        </Field>

        {/* 🟥 名前欄は任意。冒頭で「お名前は任意です」と伝えている以上、
            興味の有無に関わらずここで書けるようにする（2026-09-25 後藤さん）。
            以前は「興味がある」を選んだ人だけに出していたが、それだと
            冒頭の説明と矛盾する（伺わないと言っておいて実は欄がある、になっていた）。 */}
        <Field label="お名前" hint="任意です。書かなくても送信できます。">
          <input
            value={contactName}
            onChange={(e) => setContactName(e.target.value)}
            className={ta}
            style={{ borderColor: '#e2ded4' }}
            placeholder="お名前（任意）"
          />
        </Field>

        {error && (
          <p className="mt-6 rounded-xl px-4 py-3 text-[15px] bg-[#fdf1f1] text-[#a33]">{error}</p>
        )}

        <button
          type="button"
          onClick={submit}
          disabled={sending}
          className="mt-8 w-full rounded-xl py-4 text-[17px] font-bold text-white shadow-md transition-all active:scale-[0.98] active:shadow-sm disabled:opacity-50"
          style={{ background: GOLD }}
        >
          {sending ? '送信しています…' : '送信する'}
        </button>
        <p className="mt-3 text-center text-[14px] text-[#8a8175]">
          本日はお疲れさまでした。
        </p>
      </div>
    </main>
  );
}
